import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const guides = [
  {
    icon: "ri-search-line",
    title: "关键词搜索",
    desc: "在顶部搜索框输入影片名、导演或演员，即可快速定位想看的作品，支持模糊匹配。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    desc: "在分类页按类型、地区、年份进行组合筛选，配合排序快速缩小范围。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏",
    desc: "在详情页点击收藏即可保存心仪作品，状态仅存于当前浏览器，不会跨设备同步。",
  },
  {
    icon: "ri-share-forward-line",
    title: "分享链接",
    desc: "详情页支持一键复制作品链接，方便和朋友分享正在关注的影视内容。",
  },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 绍宋影视大全",
    description:
      "了解绍宋影视大全的内容定位、服务宗旨与联系方式。本站为影视内容浏览演示平台，提供丰富的分类与剧情介绍。",
    keywords: "绍宋,关于我们,影视平台介绍,联系方式",
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 pt-16">
        <section className="w-full bg-primary-900">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-primary-200/80 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
              <span className="text-background-50">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">关于绍宋影视</h1>
            <p className="mt-3 text-sm md:text-base text-primary-100 max-w-3xl leading-7">
              我们相信，找到一部想看的影视作品不该是一件难事。绍宋致力于用清晰的分类与真诚的介绍，让每一次浏览都轻松愉快。
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2 space-y-10">
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">内容定位</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-8">
                  绍宋是一个综合影视内容展示平台，重点汇集最新的电影与电视剧资源。内容覆盖动作、科幻、悬疑、爱情、古装、都市等多种类型，
                  并区分大陆、欧美、日韩等地区维度，帮助用户按自己的口味快速浏览。每一部作品都配有封面、类型、年份、主演与剧情简介，
                  方便用户在不观看正片的情况下也能了解故事梗概。
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">服务宗旨</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-8">
                  我们希望做一个干净、好用、信息真实的影视浏览站。页面不堆砌无关广告，不使用误导性的"播放成功"提示，
                  所有演示性质的功能（搜索、筛选、收藏）都会明确标注。你可以放心地把时间花在挑选内容上，而不是和被干扰的界面较劲。
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">使用说明</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {guides.map((item) => (
                    <div key={item.title} className="p-5 rounded-lg border border-background-200 bg-background-100">
                      <span className="inline-flex w-10 h-10 rounded-md bg-accent-100 text-accent-700 items-center justify-center mb-3">
                        <i className={`${item.icon} text-xl`} />
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-6">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-20 space-y-4">
                <div className="p-5 rounded-lg bg-primary-900">
                  <h3 className="font-heading text-base font-semibold text-background-50 mb-4">联系方式</h3>
                  <ul className="space-y-4 text-sm">
                    <li>
                      <span className="block text-primary-200 text-xs mb-1">电子邮箱</span>
                      <a
                        href="mailto:hello@shaosong-tv.example"
                        className="inline-flex items-center gap-2 text-background-50 hover:text-accent-400 transition-colors"
                      >
                        <i className="ri-mail-line text-base w-4 h-4 flex items-center justify-center" />
                        hello@shaosong-tv.example
                      </a>
                    </li>
                    <li>
                      <span className="block text-primary-200 text-xs mb-1">合作邮箱</span>
                      <a
                        href="mailto:partner@shaosong-tv.example"
                        className="inline-flex items-center gap-2 text-background-50 hover:text-accent-400 transition-colors"
                      >
                        <i className="ri-mail-send-line text-base w-4 h-4 flex items-center justify-center" />
                        partner@shaosong-tv.example
                      </a>
                    </li>
                    <li>
                      <span className="block text-primary-200 text-xs mb-1">所在地</span>
                      <span className="inline-flex items-center gap-2 text-background-50">
                        <i className="ri-map-pin-line text-base w-4 h-4 flex items-center justify-center" />
                        浙江省杭州市
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="p-5 rounded-lg bg-secondary-100 text-secondary-900">
                  <h3 className="font-heading text-base font-semibold mb-2">友情提示</h3>
                  <p className="text-xs leading-6">
                    本站所有影视信息、封面与数据均为演示内容，其中邮箱地址为示范地址。站点不含视频播放、下载或支付功能，
                    搜索与收藏均为前端演示。
                  </p>
                </div>

                <Link
                  to="/search"
                  className="flex items-center justify-between p-5 rounded-lg bg-accent-500 text-primary-950 hover:bg-accent-400 transition-colors"
                >
                  <span className="font-heading text-base font-semibold">去发现好片</span>
                  <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}